import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import {
    Loader2, ArrowRight, MessageSquare, UserPlus, Mail, Phone, Building2, Globe, TrendingUp,
    Users, HandCoins, Wallet, Crown, ShieldCheck, ShieldOff, Trash2, Pencil, Search, Receipt, Download, FileEdit, Save, Plus,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ActivationStatsPanel, ConsortiumPipelinePanel, FunnelPanel, ScoresPanel } from '@/components/app/SaGrowthPanels';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SA_SECTIONS } from '@/pages/app/superAdminSections';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';
import Pager from '@/components/Pager';
import CampusAdmin from '@/components/CampusAdmin';
import TrustedLogosAdmin from '@/components/TrustedLogosAdmin';
import StripePaymentsAdmin from '@/components/StripePaymentsAdmin';
import OnboardingFunnelAdmin from '@/components/OnboardingFunnelAdmin';
import ExecutiveDashboardAdmin from '@/components/ExecutiveDashboardAdmin';
import ApiIntegrationsAdmin from '@/components/ApiIntegrationsAdmin';
import AuditAdmin from '@/components/AuditAdmin';
import NetworkAdmin from '@/components/network/NetworkAdmin';
import NetworkCrm from '@/components/network/NetworkCrm';
import NetworkCampaigns from '@/components/network/NetworkCampaigns';
import NetworkSequences from '@/components/network/NetworkSequences';
import NetworkAnalytics from '@/components/network/NetworkAnalytics';
import GdprRegister from '@/components/tech/GdprRegister';
import SystemLog from '@/components/tech/SystemLog';
import SmtpAdmin from '@/components/tech/SmtpAdmin';
import MonitoringAdmin from '@/components/tech/MonitoringAdmin';
import PricingAdmin from '@/components/PricingAdmin';
import MarketplaceAdmin from '@/components/MarketplaceAdmin';
import CleanupAdmin from '@/components/CleanupAdmin';
import { SECTORS_ALL } from '@/lib/sectors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
    { key: 'new', label: 'Nouvelles', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    { key: 'contacted', label: 'Contactées', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    { key: 'qualified', label: 'Qualifiées', color: 'bg-purple-50 text-purple-700 border-purple-200' },
    { key: 'proposal', label: 'Proposition', color: 'bg-orange-50 text-orange-700 border-orange-200' },
    { key: 'won', label: 'Gagnées', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { key: 'lost', label: 'Perdues', color: 'bg-stone-100 text-stone-600 border-stone-200' },
];

// Lead attribution sources — wired via /apply?source=<key> query param
const SOURCE_META = {
    'campus-public':   { label: 'Campus 360°',  emoji: '🎓', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    'diagnostic-quiz': { label: 'Diagnostic',   emoji: '📊', color: 'bg-blue-100 text-blue-800 border-blue-200' },
    'landing':         { label: 'Landing',      emoji: '🏠', color: 'bg-violet-100 text-violet-800 border-violet-200' },
    'donate':          { label: 'Donation',     emoji: '❤️',  color: 'bg-rose-100 text-rose-800 border-rose-200' },
    'cms':             { label: 'Article CMS',  emoji: '📰', color: 'bg-amber-100 text-amber-800 border-amber-200' },
    'footer':          { label: 'Footer',       emoji: '🔗', color: 'bg-stone-100 text-stone-700 border-stone-200' },
    'consortium':      { label: 'CONSORTIUM',   emoji: '🤝', color: 'bg-indigo-600 text-white border-indigo-700 font-bold shadow-sm' },
    'maintenance':     { label: 'Maintenance',  emoji: '🔧', color: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
};
function SourceBadge({ source, size = 'sm' }) {
    if (!source) return null;
    const m = SOURCE_META[source];
    if (!m) {
        return <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-md bg-stone-100 text-stone-600 border border-stone-200`}>{source}</span>;
    }
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md border ${m.color} ${size === 'lg' ? 'text-xs px-2 py-1' : ''}`}>
            <span>{m.emoji}</span>{m.label}
        </span>
    );
}

// Visual badge for a workspace owner's plan tier
const PLAN_COLORS = {
    free:         'bg-stone-100 text-stone-700 border-stone-200',
    starter:      'bg-blue-50 text-blue-700 border-blue-200',
    essentiel:    'bg-teal-50 text-teal-700 border-teal-200',
    pro:          'bg-primary-light text-primary border-primary/20',
    organisation: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    unlimited:    'bg-emerald-50 text-emerald-700 border-emerald-200',
};
function PlanBadge({ tier }) {
    if (!tier) return <span className="text-xs text-muted-foreground italic">Aucun</span>;
    const cls = PLAN_COLORS[tier] || 'bg-surface-muted text-foreground/70 border-border';
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-wider border ${cls}`}>
            {tier}
        </span>
    );
}

// Compliance smiley cell for Plans Clients
const COMPLIANCE_META = {
    ok:        { emoji: '😊', label: 'Conforme',     color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    warning:   { emoji: '🔄', label: 'Changement',   color: 'bg-amber-50 text-amber-700 border-amber-200' },
    violation: { emoji: '⚠️', label: 'Non conforme', color: 'bg-red-50 text-red-700 border-red-200' },
};
function ComplianceCell({ status, reason }) {
    const meta = COMPLIANCE_META[status] || COMPLIANCE_META.ok;
    return (
        <span
            title={reason || meta.label}
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${meta.color}`}
            data-testid={`sa-plan-compliance-${status}`}
        >
            <span className="text-base leading-none">{meta.emoji}</span>
            {meta.label}
        </span>
    );
}

// ---------- Leads-by-source attribution panel ----------
function LeadsBySourcePanel({ auth }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!auth) return;
        axios.get(`${API}/super-admin/leads-by-source`, auth)
            .then(r => setData(r.data))
            .catch(() => setData({ items: [], total_leads: 0, total_this_week: 0 }))
            .finally(() => setLoading(false));
    }, [auth]);
    if (loading) return null;
    const items = data?.items || [];
    if (items.length === 0) return null;
    const max = Math.max(...items.map(i => i.total), 1);
    const bestSrc = items.reduce((a, b) => (b.conversion_rate > (a?.conversion_rate ?? -1) && b.won_count > 0 ? b : a), null);
    return (
        <div className="card-soft p-5" data-testid="sa-leads-by-source">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <div className="eyebrow mb-1 text-primary">Attribution leads</div>
                    <h2 className="font-heading text-xl">Entonnoir par provenance</h2>
                </div>
                <div className="flex items-center gap-5 text-xs">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Cette semaine</div>
                        <div className="font-heading text-lg tabular-nums text-primary">{data.total_this_week}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total leads</div>
                        <div className="font-heading text-lg tabular-nums">{data.total_leads}</div>
                    </div>
                    {bestSrc && (
                        <div className="hidden md:block">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Meilleure source</div>
                            <div className="font-heading text-lg tabular-nums text-emerald-600 flex items-center gap-1.5">
                                {(SOURCE_META[bestSrc.source]?.emoji) || '🔗'} {bestSrc.conversion_rate}%
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="space-y-2.5">
                {items.map(it => {
                    const meta = SOURCE_META[it.source] || { label: it.source, emoji: '🔗', color: 'bg-stone-100 text-stone-700 border-stone-200' };
                    const widthPct = Math.round((it.total / max) * 100);
                    return (
                        <div key={it.source} className="flex items-center gap-4" data-testid={`lead-source-row-${it.source}`}>
                            <div className="w-44 shrink-0 inline-flex items-center gap-2 text-sm">
                                <span className="text-lg leading-none">{meta.emoji}</span>
                                <span className="font-medium truncate">{meta.label}</span>
                            </div>
                            <div className="flex-1 h-7 bg-surface-muted rounded-md overflow-hidden relative">
                                <div className="h-full bg-gradient-to-r from-primary/80 to-primary transition-all duration-700 flex items-center px-3"
                                     style={{ width: `${widthPct}%`, minWidth: '60px' }}>
                                    <span className="text-xs text-primary-foreground font-medium tabular-nums">{it.total}</span>
                                </div>
                                {it.this_week > 0 && (
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-muted-foreground">
                                        +{it.this_week} cette semaine
                                    </span>
                                )}
                            </div>
                            <div className="w-32 shrink-0 flex items-center justify-end gap-3 text-xs">
                                <span className="text-emerald-600 font-mono">{it.won_count} gagnée(s)</span>
                                <span className={`px-2 py-0.5 rounded-full font-medium ${it.conversion_rate >= 20 ? 'bg-emerald-50 text-emerald-700' : it.conversion_rate >= 10 ? 'bg-amber-50 text-amber-700' : 'bg-stone-50 text-stone-600'}`}>
                                    {it.conversion_rate}%
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// Vue d'ensemble — KPIs globaux + attribution des leads
function OverviewSection({ auth }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!auth) { setLoading(false); return; }
        axios.get(`${API}/super-admin/stats`, auth).then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, [auth]);

    if (loading) return <div className="flex items-center justify-center py-20" data-testid="sa-loading"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="space-y-8">
            {stats && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    <StatCard icon={Users} label="Demandes" value={stats.leads_total} sub={`${stats.leads_new} nouvelles`} testid="stat-leads" />
                    <StatCard icon={TrendingUp} label="Conversion" value={`${stats.conversion_rate}%`} sub={`${stats.leads_won} gagnées`} testid="stat-conversion" />
                    <StatCard icon={Building2} label="Espaces clients" value={stats.workspaces_total} testid="stat-workspaces" />
                    <StatCard icon={Wallet} label="Revenus (EUR)" value={`${stats.revenue_eur.toLocaleString('fr-FR')}€`} sub="hors dons" testid="stat-revenue" />
                    <StatCard icon={HandCoins} label="Dons reçus" value={`${stats.donations_total_eur.toLocaleString('fr-FR')}€`} sub={`${stats.donations_total_count} dons`} testid="stat-donations" />
                </div>
            )}
            <div className="grid lg:grid-cols-2 gap-6">
                <FunnelPanel auth={auth} />
                <ScoresPanel auth={auth} />
                <ActivationStatsPanel auth={auth} />
                <ConsortiumPipelinePanel auth={auth} />
            </div>
            <LeadsBySourcePanel auth={auth} />
        </div>
    );
}

export default function SuperAdmin() {
    const { section } = useParams();
    const { session, profile, refreshProfile } = useAuth();
    const auth = useMemo(() => session?.access_token ? { headers: { Authorization: `Bearer ${session.access_token}` } } : null, [session]);

    useEffect(() => { if (refreshProfile) refreshProfile(); /* eslint-disable-next-line */ }, []);

    const key = section || 'overview';
    const meta = SA_SECTIONS[key];

    if (profile === null) return <div className="flex items-center justify-center py-20" data-testid="sa-loading"><Loader2 className="animate-spin" /></div>;
    if (!profile?.is_super_admin) {
        return <div className="text-center py-20 text-foreground/60" data-testid="sa-forbidden">Accès réservé au Super Admin.</div>;
    }
    if (!meta) return <Navigate to="/app/super-admin" replace />;

    const SECTION_COMPONENTS = {
        overview: <OverviewSection auth={auth} />,
        executive: <ExecutiveDashboardAdmin auth={auth} />,
        'onb-funnel': <OnboardingFunnelAdmin auth={auth} />,
        crm: <CrmTab auth={auth} />,
        network: <NetworkAdmin auth={auth} />,
        'network-crm': <NetworkCrm auth={auth} />,
        'network-campaigns': <NetworkCampaigns auth={auth} />,
        'network-sequences': <NetworkSequences auth={auth} />,
        'network-analytics': <NetworkAnalytics auth={auth} />,
        workspaces: <WorkspacesTab auth={auth} />,
        users: <UsersTab auth={auth} currentUserId={profile?.id} />,
        plans: <PlansTab auth={auth} />,
        pricing: <PricingAdmin auth={auth} />,
        marketplace: <MarketplaceAdmin auth={auth} />,
        cleanup: <CleanupAdmin auth={auth} />,
        payments: <PaymentsTab auth={auth} />,
        donations: <DonationsTab auth={auth} />,
        stripe: <StripePaymentsAdmin auth={auth} />,
        pages: <PagesTab auth={auth} />,
        campus: <CampusAdmin auth={auth} />,
        trusted: <TrustedLogosAdmin auth={auth} />,
        api: <ApiIntegrationsAdmin />,
        audit: <AuditAdmin auth={auth} />,
        'tech-gdpr': <GdprRegister auth={auth} />,
        'tech-logs': <SystemLog auth={auth} />,
        'tech-smtp': <SmtpAdmin auth={auth} />,
        'tech-monitoring': <MonitoringAdmin auth={auth} />,
    };

    const Icon = meta.icon;

    return (
        <div className="space-y-8" data-testid="super-admin-page">
            <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                    <Icon size={22} strokeWidth={1.8} />
                </div>
                <div>
                    <div className="eyebrow mb-1" data-testid="sa-section-eyebrow">Super Admin · {meta.eyebrow}</div>
                    <h1 className="font-heading text-3xl lg:text-4xl" data-testid="sa-section-title">{meta.label}</h1>
                    <p className="text-sm text-muted-foreground mt-1" data-testid="sa-section-description">{meta.description}</p>
                </div>
            </div>
            <div data-testid={`sa-section-${key}`}>{SECTION_COMPONENTS[key]}</div>
        </div>
    );
}

// =============================================================================
// CRM TAB (Kanban tunnel de vente)
// =============================================================================
function CrmTab({ auth }) {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [promoteOpen, setPromoteOpen] = useState(false);
    const [promoteForm, setPromoteForm] = useState({ workspace_name: '', admin_password: '', industry: '', country: '' });
    const [sourceFilter, setSourceFilter] = useState('all');

    const reload = async () => {
        if (!auth) { setLoading(false); return; }
        setLoading(true);
        try {
            const r = await axios.get(`${API}/access-requests`, auth);
            setRequests(r.data.items || []);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        finally { setLoading(false); }
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [auth]);

    const openDetail = async (req) => {
        setDetail(req);
        try {
            const r = await axios.get(`${API}/access-requests/${req.id}`, auth);
            setNotes(r.data.notes || []);
        } catch { setNotes([]); }
    };

    const moveStage = async (req, stage) => {
        setBusy(true);
        try {
            await axios.patch(`${API}/access-requests/${req.id}`, { stage }, auth);
            toast.success(`Étape mise à jour → ${stage}`);
            reload();
            if (detail?.id === req.id) setDetail({ ...detail, stage });
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    const addNote = async () => {
        if (!newNote.trim() || !detail) return;
        try {
            await axios.post(`${API}/access-requests/${detail.id}/notes`, { content: newNote.trim() }, auth);
            setNewNote('');
            const r = await axios.get(`${API}/access-requests/${detail.id}`, auth);
            setNotes(r.data.notes || []);
            toast.success('Note ajoutée');
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const openPromote = () => {
        if (!detail) return;
        setPromoteForm({
            workspace_name: detail.organization || detail.full_name,
            admin_password: '',
            industry: detail.sector || '',
            country: detail.country || '',
        });
        setPromoteOpen(true);
    };

    const promote = async (e) => {
        e.preventDefault();
        if (promoteForm.admin_password.length < 8) { toast.error('Mot de passe ≥ 8 caractères'); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/access-requests/${detail.id}/promote`, promoteForm, auth);
            toast.success('Compte client créé. Email de bienvenue envoyé.');
            setPromoteOpen(false);
            setDetail(null);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    const deleteRequest = async (req) => {
        if (!window.confirm(`Supprimer définitivement la fiche de ${req.full_name || req.email} ? Cette action est irréversible.`)) return;
        setBusy(true);
        try {
            await axios.delete(`${API}/access-requests/${req.id}`, auth);
            toast.success('Fiche supprimée');
            if (detail?.id === req.id) setDetail(null);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin" /></div>;

    const exportCsv = async () => {
        try {
            const res = await axios.get(`${API}/super-admin/leads/export.csv`, { ...auth, responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `african-valley-leads-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1500);
            toast.success(`Export CSV téléchargé (${requests.length} lead(s))`);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    return (
        <>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="text-sm text-muted-foreground">{requests.length} demande(s) au total{sourceFilter !== 'all' && <> · <strong>{requests.filter(r => (r.source || 'landing') === sourceFilter).length}</strong> via <SourceBadge source={sourceFilter} /></>}</div>
                <Button variant="outline" size="sm" onClick={exportCsv} data-testid="sa-crm-export-csv">
                    <Download size={14} className="mr-1.5" /> Exporter CSV
                </Button>
            </div>
            {/* Source attribution filter */}
            <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="crm-source-filters">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Provenance :</span>
                <button onClick={() => setSourceFilter('all')}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${sourceFilter === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary/40'}`}
                        data-testid="crm-filter-all">
                    Toutes ({requests.length})
                </button>
                {Object.entries(SOURCE_META).map(([key, meta]) => {
                    const count = requests.filter(r => (r.source || 'landing') === key).length;
                    if (count === 0 && sourceFilter !== key) return null;
                    return (
                        <button key={key} onClick={() => setSourceFilter(key)}
                                className={`text-xs px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1 ${sourceFilter === key ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary/40'}`}
                                data-testid={`crm-filter-${key}`}>
                            <span>{meta.emoji}</span> {meta.label} ({count})
                        </button>
                    );
                })}
            </div>
            <div className="grid lg:grid-cols-6 gap-3 overflow-x-auto pb-4">
                {STAGES.map(s => {
                    const items = requests.filter(r => r.stage === s.key && (sourceFilter === 'all' || (r.source || 'landing') === sourceFilter));
                    return (
                        <div key={s.key} className="min-w-[180px]" data-testid={`crm-column-${s.key}`}>
                            <div className={`px-3 py-2 rounded-t-md text-xs font-semibold uppercase tracking-wider border ${s.color}`}>
                                {s.label} <span className="opacity-60">({items.length})</span>
                            </div>
                            <div className="bg-surface-muted/40 border border-t-0 border-border rounded-b-md p-2 space-y-2 min-h-[200px]">
                                {items.length === 0 ? (
                                    <div className="text-xs text-foreground/40 text-center py-4">—</div>
                                ) : items.map(r => (
                                    <div key={r.id} className="relative group">
                                        <button onClick={() => openDetail(r)}
                                            className="w-full text-left bg-surface border border-border rounded-md p-3 hover:shadow-soft hover:border-primary/30 transition-all"
                                            data-testid={`crm-card-${r.id}`}>
                                            <div className="text-sm font-medium truncate pr-6">{r.full_name}</div>
                                            <div className="text-xs text-muted-foreground truncate">{r.organization || r.email}</div>
                                            <div className="flex items-center justify-between mt-1.5 gap-2">
                                                <span className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString('fr-FR')}</span>
                                                <SourceBadge source={r.source} />
                                            </div>
                                        </button>
                                        {!r.converted_workspace_id && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); deleteRequest(r); }}
                                                className="absolute top-2 right-2 p-1 rounded text-destructive opacity-0 group-hover:opacity-100 hover:bg-destructive/10 transition-all"
                                                title="Supprimer la fiche"
                                                data-testid={`crm-card-delete-${r.id}`}
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
                <DialogContent className="max-w-2xl">
                    {detail && (
                        <>
                            <DialogHeader><DialogTitle className="font-heading text-2xl">{detail.full_name || `${detail.first_name || ''} ${detail.last_name || ''}`.trim() || detail.email}</DialogTitle></DialogHeader>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <Field icon={Mail} label="Email" value={detail.email} />
                                    <Field icon={Phone} label="Téléphone" value={detail.phone} />
                                    <Field icon={Building2} label="Organisation" value={detail.organization} />
                                    <Field label="Raison sociale" value={detail.legal_entity} />
                                    <Field icon={Globe} label="Pays" value={detail.country} />
                                    <Field label="Secteur" value={detail.sector} />
                                    <Field label="Équipe" value={detail.team_size} />
                                    <Field label="Fonds à gérer" value={detail.funds_to_manage_amount ? `${Number(detail.funds_to_manage_amount).toLocaleString('fr-FR')} ${detail.funds_to_manage_currency || 'EUR'}` : null} />
                                    <Field label="Source de la connaissance" value={detail.how_did_you_hear} />
                                </div>
                                {detail.message && <div className="bg-surface-muted p-3 rounded-md text-sm italic">« {detail.message} »</div>}
                                <div className="flex gap-2 flex-wrap pt-2 border-t border-border">
                                    {STAGES.map(s => (
                                        <Button key={s.key} size="sm" variant={detail.stage === s.key ? 'default' : 'outline'}
                                            onClick={() => moveStage(detail, s.key)} disabled={busy || detail.stage === s.key}
                                            className={detail.stage === s.key ? 'bg-primary' : ''} data-testid={`crm-stage-btn-${s.key}`}>
                                            <ArrowRight size={12} className="mr-1" /> {s.label}
                                        </Button>
                                    ))}
                                </div>
                                {!detail.converted_workspace_id && (
                                    <Button onClick={openPromote} className="w-full bg-primary hover:bg-primary-hover" data-testid="crm-promote-btn">
                                        <UserPlus size={14} className="mr-2" /> Créer le Compte Client (workspace + admin)
                                    </Button>
                                )}
                                {!detail.converted_workspace_id && (
                                    <Button
                                        onClick={() => deleteRequest(detail)}
                                        variant="outline"
                                        className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/30"
                                        disabled={busy}
                                        data-testid="crm-delete-btn"
                                    >
                                        <Trash2 size={14} className="mr-2" /> Supprimer définitivement cette fiche
                                    </Button>
                                )}
                                {detail.converted_workspace_id && (
                                    <div className="text-xs text-emerald-700 bg-emerald-50 p-3 rounded-md border border-emerald-200">
                                        ✓ Compte client créé · workspace <code>{detail.converted_workspace_id.slice(0, 8)}…</code>
                                    </div>
                                )}
                                <div className="pt-3 border-t border-border">
                                    <div className="text-sm font-semibold mb-2 flex items-center gap-2"><MessageSquare size={14} /> Historique</div>
                                    <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                                        {notes.length === 0 ? <div className="text-xs text-foreground/50">Aucune note.</div> : notes.map(n => (
                                            <div key={n.id} className="text-xs bg-surface-muted p-2 rounded">
                                                <div className="text-foreground/60 mb-1">{new Date(n.created_at).toLocaleString('fr-FR')}</div>
                                                <div>{n.content}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <Input placeholder="Note (appel, réunion, email…)" value={newNote}
                                            onChange={(e) => setNewNote(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && addNote()} data-testid="crm-note-input" />
                                        <Button size="sm" onClick={addNote} data-testid="crm-note-add">Ajouter</Button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Créer le Compte Client</DialogTitle></DialogHeader>
                    <form onSubmit={promote} className="space-y-3">
                        <Input required placeholder="Nom du workspace" value={promoteForm.workspace_name}
                            onChange={(e) => setPromoteForm({ ...promoteForm, workspace_name: e.target.value })} data-testid="promote-workspace-name" />
                        <Input required type="text" placeholder="Mot de passe initial admin (≥ 8 car.)" value={promoteForm.admin_password}
                            onChange={(e) => setPromoteForm({ ...promoteForm, admin_password: e.target.value })} data-testid="promote-password" />
                        <div className="grid grid-cols-2 gap-3">
                            <select
                                className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                                value={promoteForm.industry}
                                onChange={(e) => setPromoteForm({ ...promoteForm, industry: e.target.value })}
                                data-testid="promote-industry"
                            >
                                <option value="">— Choisir un secteur —</option>
                                {SECTORS_ALL.map(s => (
                                    <option key={s.key} value={s.name}>{s.emoji} {s.name}</option>
                                ))}
                            </select>
                            <Input placeholder="Pays" value={promoteForm.country}
                                onChange={(e) => setPromoteForm({ ...promoteForm, country: e.target.value })} />
                        </div>
                        <p className="text-xs text-muted-foreground">14 secteurs disponibles · jalons sectoriels appliqués automatiquement à chaque projet créé. L'admin recevra un email avec ses identifiants (si SMTP configuré).</p>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setPromoteOpen(false)}>Annuler</Button>
                            <Button type="submit" disabled={busy} className="bg-primary hover:bg-primary-hover" data-testid="promote-submit">
                                {busy ? <Loader2 size={14} className="animate-spin" /> : 'Créer'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}

// =============================================================================
// WORKSPACES TAB
// =============================================================================
function WorkspacesTab({ auth }) {
    const PAGE_SIZE = 30;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [editing, setEditing] = useState(null); // {id, name}
    const [busy, setBusy] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [confirmName, setConfirmName] = useState('');

    const reload = async () => {
        if (!auth) { setLoading(false); return; }
        setLoading(true);
        try {
            const r = await axios.get(`${API}/super-admin/workspaces`, auth);
            setItems(r.data.items || []);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        finally { setLoading(false); }
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [auth]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? items.filter(w => (w.name || '').toLowerCase().includes(q) || (w.country || '').toLowerCase().includes(q) || (w.industry || '').toLowerCase().includes(q)) : items;
    }, [items, search]);

    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    useEffect(() => { setPage(1); }, [search]);

    const saveName = async () => {
        if (!editing?.name?.trim() || editing.name.trim().length < 2) { toast.error('Nom trop court'); return; }
        setBusy(true);
        try {
            await axios.patch(`${API}/workspaces/${editing.id}`, { name: editing.name.trim() }, auth);
            toast.success('Espace renommé');
            setEditing(null);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    const normalize = (s) => (s || '').replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();
    const doDelete = async () => {
        if (normalize(confirmName) !== normalize(deleteTarget?.name)) { toast.error('Nom de confirmation incorrect'); return; }
        setBusy(true);
        try {
            await axios.delete(`${API}/workspaces/${deleteTarget.id}`, {
                ...auth, params: { confirm_name: normalize(deleteTarget.name) },
            });
            toast.success('Espace supprimé');
            setDeleteTarget(null); setConfirmName('');
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="space-y-4" data-testid="sa-workspaces">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="relative w-full max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                    <Input className="pl-9" placeholder="Rechercher un espace, pays, secteur…" value={search}
                        onChange={(e) => setSearch(e.target.value)} data-testid="sa-ws-search" />
                </div>
                <div className="text-sm text-muted-foreground">{filtered.length} espace(s)</div>
            </div>
            <div className="card-soft overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Nom</th>
                            <th className="px-4 py-3">Secteur</th>
                            <th className="px-4 py-3">Pays</th>
                            <th className="px-4 py-3 text-center">Membres</th>
                            <th className="px-4 py-3">Plan</th>
                            <th className="px-4 py-3">Créé le</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {paged.map(w => (
                            <tr key={w.id} data-testid={`sa-ws-row-${w.id}`}>
                                <td className="px-4 py-3">
                                    {editing?.id === w.id ? (
                                        <div className="flex gap-2">
                                            <Input className="h-8 text-sm" value={editing.name}
                                                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                                onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditing(null); }}
                                                autoFocus />
                                            <Button size="sm" onClick={saveName} disabled={busy} className="bg-primary hover:bg-primary-hover h-8">
                                                {busy ? <Loader2 size={12} className="animate-spin" /> : 'OK'}
                                            </Button>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="font-medium">{w.name}</div>
                                            <div className="text-xs text-muted-foreground">{w.is_training ? 'Espace formation' : 'Espace production'} · <code>{w.id.slice(0, 8)}</code></div>
                                        </>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-muted-foreground">{w.industry || '—'}</td>
                                <td className="px-4 py-3 text-muted-foreground">{w.country || '—'}</td>
                                <td className="px-4 py-3 text-center">{w.members_count || 0}</td>
                                <td className="px-4 py-3" data-testid={`sa-ws-plan-${w.id}`}>
                                    <PlanBadge tier={w.plan_tier} />
                                </td>
                                <td className="px-4 py-3 text-muted-foreground text-xs">{new Date(w.created_at).toLocaleDateString('fr-FR')}</td>
                                <td className="px-4 py-3 text-right">
                                    <Button size="sm" variant="ghost" onClick={() => setEditing({ id: w.id, name: w.name })}
                                        data-testid={`sa-ws-edit-${w.id}`}>
                                        <Pencil size={13} />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive"
                                        onClick={() => { setDeleteTarget(w); setConfirmName(''); }}
                                        data-testid={`sa-ws-delete-${w.id}`}>
                                        <Trash2 size={13} />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr><td colSpan={7} className="px-4 py-10 text-center text-foreground/50">Aucun espace.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <Pager total={filtered.length} page={page} pageSize={PAGE_SIZE} onPage={setPage} testid="sa-ws-pager" />

            <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl text-destructive">Supprimer l'espace</DialogTitle>
                        <DialogDescription>
                            Cette action est <strong>irréversible</strong>. Tous les projets, documents, dépenses, réunions et
                            membres de cet espace seront définitivement supprimés.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm">Saisissez exactement : <strong>{deleteTarget?.name}</strong></p>
                        <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
                            placeholder={deleteTarget?.name} autoFocus data-testid="sa-ws-delete-confirm" />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Annuler</Button>
                        <Button variant="destructive" onClick={doDelete}
                            disabled={busy || normalize(confirmName) !== normalize(deleteTarget?.name)}
                            data-testid="sa-ws-delete-confirm-btn">
                            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
                            Supprimer définitivement
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// =============================================================================
// USERS TAB
// =============================================================================
function UsersTab({ auth, currentUserId }) {
    const PAGE_SIZE = 30;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // single user to delete
    const [showBulkConfirm, setShowBulkConfirm] = useState(false);

    const reload = async () => {
        if (!auth) { setLoading(false); return; }
        setLoading(true);
        try {
            const r = await axios.get(`${API}/super-admin/users`, auth);
            setItems(r.data.items || []);
            setSelected(new Set());
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        finally { setLoading(false); }
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [auth]);

    const toggleSA = async (u) => {
        if (u.id === currentUserId) { toast.error('Impossible de modifier votre propre statut'); return; }
        const action = u.is_super_admin ? 'rétrograder' : 'promouvoir en Super Admin';
        if (!window.confirm(`Confirmer : ${action} ${u.email} ?`)) return;
        setBusyId(u.id);
        try {
            const r = await axios.post(`${API}/super-admin/users/${u.id}/toggle-super-admin`, {}, auth);
            toast.success(`${u.email} : Super Admin = ${r.data.is_super_admin ? 'OUI' : 'NON'}`);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusyId(null);
    };

    const toggleAdrns = async (u) => {
        const action = u.adrns_member
            ? `Retirer l'accès à l'Espace ADRNS pour ${u.email} ?`
            : `Affecter l'Espace ADRNS à ${u.email} (rôle : accompagnateur) ?`;
        if (!window.confirm(action)) return;
        setBusyId(u.id);
        try {
            const r = await axios.post(`${API}/super-admin/users/${u.id}/toggle-adrns`, {}, auth);
            toast.success(r.data.adrns_member
                ? `${u.email} a désormais accès à l'Espace ADRNS`
                : `Accès ADRNS retiré pour ${u.email}`);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusyId(null);
    };

    const doSingleDelete = async () => {
        if (!deleteTarget) return;
        setBusyId(deleteTarget.id);
        try {
            await axios.delete(`${API}/super-admin/users/${deleteTarget.id}`, auth);
            toast.success(`Utilisateur ${deleteTarget.email} supprimé`);
            setDeleteTarget(null);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusyId(null);
    };

    const doBulkDelete = async () => {
        const ids = Array.from(selected);
        if (!ids.length) return;
        setBulkBusy(true);
        try {
            const r = await axios.post(`${API}/super-admin/users/bulk-delete`, { user_ids: ids }, auth);
            toast.success(`${r.data.count_deleted} supprimé(s) · ${r.data.count_skipped} ignoré(s)`);
            if (r.data.skipped?.length) {
                console.warn('Bulk delete skipped:', r.data.skipped);
            }
            setShowBulkConfirm(false);
            setSelected(new Set());
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBulkBusy(false);
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? items.filter(u => (u.email || '').toLowerCase().includes(q) || (u.full_name || '').toLowerCase().includes(q)) : items;
    }, [items, search]);

    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    useEffect(() => { setPage(1); }, [search]);

    const toggleSelect = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const allPagedSelected = paged.length > 0 && paged.every(u => u.id === currentUserId || u.is_super_admin || selected.has(u.id));
    const toggleSelectAllOnPage = () => {
        setSelected(prev => {
            const next = new Set(prev);
            const selectable = paged.filter(u => u.id !== currentUserId && !u.is_super_admin);
            if (allPagedSelected) {
                selectable.forEach(u => next.delete(u.id));
            } else {
                selectable.forEach(u => next.add(u.id));
            }
            return next;
        });
    };

    if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="space-y-4" data-testid="sa-users">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="relative w-full max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                    <Input className="pl-9" placeholder="Rechercher un utilisateur, email…" value={search}
                        onChange={(e) => setSearch(e.target.value)} data-testid="sa-users-search" />
                </div>
                <div className="flex items-center gap-3">
                    {selected.size > 0 && (
                        <Button
                            variant="destructive" size="sm"
                            onClick={() => setShowBulkConfirm(true)}
                            disabled={bulkBusy}
                            data-testid="sa-users-bulk-delete"
                        >
                            <Trash2 size={13} className="mr-1.5" /> Supprimer la sélection ({selected.size})
                        </Button>
                    )}
                    <div className="text-sm text-muted-foreground">{filtered.length} utilisateur(s)</div>
                </div>
            </div>
            <div className="card-soft overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-3 w-10">
                                <input type="checkbox" checked={allPagedSelected} onChange={toggleSelectAllOnPage} className="rounded" data-testid="sa-users-select-all" />
                            </th>
                            <th className="px-4 py-3">Utilisateur</th>
                            <th className="px-4 py-3">Langue</th>
                            <th className="px-4 py-3 text-center">Super Admin</th>
                            <th className="px-4 py-3 text-center">Espace ADRNS</th>
                            <th className="px-4 py-3">Créé le</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {paged.map(u => {
                            const protectedRow = u.id === currentUserId || u.is_super_admin;
                            return (
                                <tr key={u.id} data-testid={`sa-user-row-${u.id}`} className={selected.has(u.id) ? 'bg-primary/[0.04]' : ''}>
                                    <td className="px-3 py-3">
                                        <input
                                            type="checkbox"
                                            checked={selected.has(u.id)}
                                            disabled={protectedRow}
                                            onChange={() => toggleSelect(u.id)}
                                            className="rounded"
                                            data-testid={`sa-user-select-${u.id}`}
                                        />
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="font-medium">{u.full_name || '—'}</div>
                                        <div className="text-xs text-muted-foreground">{u.email}</div>
                                    </td>
                                    <td className="px-4 py-3 uppercase text-xs">{u.preferred_language || 'fr'}</td>
                                    <td className="px-4 py-3 text-center">
                                        {u.is_super_admin
                                            ? <span className="inline-flex items-center gap-1 text-xs bg-primary text-primary-foreground px-2 py-1 rounded-full"><Crown size={12} /> OUI</span>
                                            : <span className="text-xs text-muted-foreground">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <button
                                            onClick={() => toggleAdrns(u)}
                                            disabled={busyId === u.id}
                                            title={u.adrns_member ? 'Cliquer pour retirer l\'accès ADRNS' : 'Cliquer pour affecter l\'Espace ADRNS'}
                                            data-testid={`sa-user-adrns-toggle-${u.id}`}
                                            className={u.adrns_member
                                                ? 'inline-flex items-center gap-1 text-xs bg-emerald-600 text-white px-2 py-1 rounded-full hover:bg-emerald-700 transition-colors'
                                                : 'inline-flex items-center gap-1 text-xs border border-dashed border-border text-muted-foreground px-2 py-1 rounded-full hover:border-emerald-600 hover:text-emerald-600 transition-colors'}>
                                            {busyId === u.id
                                                ? <Loader2 size={11} className="animate-spin" />
                                                : u.adrns_member
                                                    ? <><ShieldCheck size={11} /> {u.adrns_role === 'coordinateur' ? 'Coordinateur' : 'Accompagnateur'}</>
                                                    : '+ Affecter'}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs">{new Date(u.created_at).toLocaleDateString('fr-FR')}</td>
                                    <td className="px-4 py-3 text-right">
                                        {u.id !== currentUserId && (
                                            <>
                                                <Button size="sm" variant={u.is_super_admin ? 'outline' : 'default'}
                                                    onClick={() => toggleSA(u)} disabled={busyId === u.id}
                                                    className={u.is_super_admin ? 'mr-1' : 'bg-primary hover:bg-primary-hover mr-1'}
                                                    data-testid={`sa-user-toggle-${u.id}`}>
                                                    {busyId === u.id ? <Loader2 size={12} className="animate-spin" />
                                                        : u.is_super_admin
                                                            ? <><ShieldOff size={13} className="mr-1.5" /> Rétrograder</>
                                                            : <><ShieldCheck size={13} className="mr-1.5" /> Promouvoir</>}
                                                </Button>
                                                {!u.is_super_admin && (
                                                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive"
                                                        onClick={() => setDeleteTarget(u)} disabled={busyId === u.id}
                                                        data-testid={`sa-user-delete-${u.id}`}>
                                                        <Trash2 size={13} />
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                        {u.id === currentUserId && <span className="text-xs text-muted-foreground italic">vous</span>}
                                    </td>
                                </tr>
                            );
                        })}
                        {paged.length === 0 && (
                            <tr><td colSpan={7} className="px-4 py-10 text-center text-foreground/50">Aucun utilisateur.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <Pager total={filtered.length} page={page} pageSize={PAGE_SIZE} onPage={setPage} testid="sa-users-pager" />

            <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl text-destructive">Supprimer l'utilisateur</DialogTitle>
                        <DialogDescription>
                            Cette action est <strong>irréversible</strong>. Le compte de <strong>{deleteTarget?.email}</strong> sera
                            définitivement supprimé : profil, adhésions aux espaces, plan, et compte d'authentification.
                            Les espaces dont il est propriétaire ne seront pas affectés (vous serez invité·e à les transférer ou les supprimer au préalable).
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Annuler</Button>
                        <Button variant="destructive" onClick={doSingleDelete} disabled={busyId === deleteTarget?.id}
                            data-testid="sa-user-delete-confirm">
                            {busyId === deleteTarget?.id ? <Loader2 size={14} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
                            Supprimer définitivement
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl text-destructive">Suppression groupée</DialogTitle>
                        <DialogDescription>
                            Supprimer définitivement <strong>{selected.size}</strong> utilisateur(s) ?
                            Les Super Admins et les propriétaires d'espaces seront automatiquement ignorés.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowBulkConfirm(false)}>Annuler</Button>
                        <Button variant="destructive" onClick={doBulkDelete} disabled={bulkBusy}
                            data-testid="sa-users-bulk-delete-confirm">
                            {bulkBusy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
                            Supprimer définitivement
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// =============================================================================
// PAYMENTS TAB
// =============================================================================
function PaymentsTab({ auth }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!auth) { setLoading(false); return; }
        setLoading(true);
        axios.get(`${API}/super-admin/payments?limit=200`, auth)
            .then(r => setItems(r.data.items || []))
            .catch(e => toast.error(e.response?.data?.detail || e.message))
            .finally(() => setLoading(false));
    }, [auth]);

    if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin" /></div>;

    const totalEur = items.reduce((s, p) => {
        const amt = Number(p.amount || 0);
        const rate = Number(p.fx_rate || 1);
        return s + (rate ? amt / rate : amt);
    }, 0);

    return (
        <div className="space-y-4" data-testid="sa-payments">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-sm">{items.length} transaction(s) — total <strong className="text-primary">{totalEur.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}€</strong></div>
            </div>
            <div className="card-soft overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Date</th>
                            <th className="px-4 py-3">Espace</th>
                            <th className="px-4 py-3">Type</th>
                            <th className="px-4 py-3">Pack</th>
                            <th className="px-4 py-3 text-right">Montant</th>
                            <th className="px-4 py-3">Référence</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {items.map(p => (
                            <tr key={p.id} data-testid={`sa-payment-row-${p.id}`}>
                                <td className="px-4 py-3 text-muted-foreground text-xs">{new Date(p.created_at).toLocaleString('fr-FR')}</td>
                                <td className="px-4 py-3">{p.workspaces?.name || <code className="text-xs">{p.workspace_id?.slice(0, 8)}</code>}</td>
                                <td className="px-4 py-3"><KindBadge kind={p.kind} /></td>
                                <td className="px-4 py-3 text-muted-foreground">{p.template_packs?.name || '—'}</td>
                                <td className="px-4 py-3 text-right font-medium">{Number(p.amount).toLocaleString('fr-FR')} {p.currency}</td>
                                <td className="px-4 py-3"><code className="text-xs text-muted-foreground">{p.reference}</code></td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-10 text-center text-foreground/50">Aucun paiement.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// =============================================================================
// DONATIONS TAB
// =============================================================================
function DonationsTab({ auth }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!auth) { setLoading(false); return; }
        setLoading(true);
        axios.get(`${API}/super-admin/donations?limit=200`, auth)
            .then(r => setItems(r.data.items || []))
            .catch(e => toast.error(e.response?.data?.detail || e.message))
            .finally(() => setLoading(false));
    }, [auth]);

    if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin" /></div>;

    const totalEur = items.reduce((s, d) => s + Number(d.amount_eur || d.amount || 0), 0);

    return (
        <div className="space-y-4" data-testid="sa-donations">
            <div className="text-sm">{items.length} don(s) — total <strong className="text-primary">{totalEur.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}€</strong></div>
            <div className="card-soft overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Date</th>
                            <th className="px-4 py-3">Donateur</th>
                            <th className="px-4 py-3">Message</th>
                            <th className="px-4 py-3 text-right">Montant</th>
                            <th className="px-4 py-3 text-center">Public</th>
                            <th className="px-4 py-3">Reçu</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {items.map(d => (
                            <tr key={d.id} data-testid={`sa-donation-row-${d.id}`}>
                                <td className="px-4 py-3 text-muted-foreground text-xs">{new Date(d.created_at).toLocaleString('fr-FR')}</td>
                                <td className="px-4 py-3">
                                    <div className="font-medium">{d.donor_name || 'Anonyme'}</div>
                                    <div className="text-xs text-muted-foreground">{d.donor_email || '—'}</div>
                                </td>
                                <td className="px-4 py-3 text-xs italic text-foreground/70 max-w-xs truncate">{d.message || '—'}</td>
                                <td className="px-4 py-3 text-right">
                                    <div className="font-medium">{Number(d.amount).toLocaleString('fr-FR')} {d.currency}</div>
                                    <div className="text-xs text-muted-foreground">≈ {Number(d.amount_eur || 0).toLocaleString('fr-FR')} €</div>
                                </td>
                                <td className="px-4 py-3 text-center">{d.is_public ? '✓' : '—'}</td>
                                <td className="px-4 py-3">
                                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/donations/${d.id}/receipt.pdf`} target="_blank" rel="noreferrer"
                                        className="text-primary underline text-xs">PDF</a>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-10 text-center text-foreground/50">Aucun don reçu.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// =============================================================================
// HELPERS
// =============================================================================
const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
    <div className="card-soft p-4" data-testid={testid}>
        <div className="flex items-center justify-between mb-2">
            <span className="eyebrow">{label}</span>
            <Icon size={14} className="text-primary" />
        </div>
        <div className="font-heading text-2xl">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
);

const Field = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-2">
        {Icon && <Icon size={14} className="text-muted-foreground mt-0.5" />}
        <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-sm truncate">{value || '—'}</div>
        </div>
    </div>
);

const KindBadge = ({ kind }) => {
    const map = {
        white_label_oneshot: { label: 'White-Label', cls: 'bg-purple-50 text-purple-700 border-purple-200' },
        pack_subscription: { label: 'Pack', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
        pack_renewal: { label: 'Renouv.', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
        refund: { label: 'Remboursement', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    };
    const m = map[kind] || { label: kind, cls: 'bg-stone-100 text-stone-700' };
    return <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${m.cls}`}>{m.label}</span>;
};

// =============================================================================
// PAGES ÉDITORIALES TAB (CMS V7)
// =============================================================================
function PagesTab({ auth }) {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeSlug, setActiveSlug] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);

    const reload = async () => {
        if (!auth) { setLoading(false); return; }
        setLoading(true);
        try {
            const r = await axios.get(`${API}/super-admin/pages`, auth);
            const items = r.data.items || [];
            setPages(items);
            if (items.length && !activeSlug) {
                setActiveSlug(items[0].slug);
                setDraft(items[0]);
            }
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        finally { setLoading(false); }
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [auth]);

    const selectPage = (slug) => {
        setActiveSlug(slug);
        const p = pages.find(x => x.slug === slug);
        setDraft(p ? { ...p } : null);
    };

    const save = async () => {
        if (!draft) return;
        if (!draft.title_fr?.trim()) { toast.error('Titre requis'); return; }
        setBusy(true);
        try {
            const body = {
                slug: draft.slug,
                title_fr: draft.title_fr,
                title_en: draft.title_en || null,
                content_html: draft.content_html || '',
                is_published: draft.is_published !== false,
                group_key: draft.group_key || null,
                position: Number(draft.position) || 0,
            };
            const r = await axios.put(`${API}/super-admin/pages/${draft.slug}`, body, auth);
            setPages(prev => prev.map(p => p.slug === draft.slug ? r.data.page : p));
            setDraft(r.data.page);
            toast.success('Page enregistrée');
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin" /></div>;

    const grouped = {
        about360: pages.filter(p => p.group_key === 'about360').sort((a, b) => (a.position || 0) - (b.position || 0)),
        legal: pages.filter(p => p.group_key === 'legal').sort((a, b) => (a.position || 0) - (b.position || 0)),
        other: pages.filter(p => p.group_key !== 'about360' && p.group_key !== 'legal'),
    };

    return (
        <div className="grid lg:grid-cols-12 gap-6" data-testid="sa-pages-tab">
            <aside className="lg:col-span-3 card-soft p-4 space-y-4 max-h-[80vh] overflow-y-auto">
                {[
                    { key: 'about360', label: 'African Valley Projet 360' },
                    { key: 'legal', label: 'Légal' },
                    { key: 'other', label: 'Autres' },
                ].map(g => grouped[g.key].length > 0 && (
                    <div key={g.key}>
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">{g.label}</div>
                        <div className="space-y-1">
                            {grouped[g.key].map(p => (
                                <button key={p.slug} onClick={() => selectPage(p.slug)}
                                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${activeSlug === p.slug ? 'bg-primary-light text-primary font-medium' : 'hover:bg-surface-muted'}`}
                                    data-testid={`sa-page-tab-${p.slug}`}>
                                    {p.title_fr}
                                    {!p.is_published && <span className="ml-1.5 text-[10px] text-muted-foreground">(masquée)</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </aside>

            <div className="lg:col-span-9 card-soft p-6">
                {!draft && <div className="text-muted-foreground">Sélectionnez une page à éditer.</div>}
                {draft && (
                    <div className="space-y-5">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <div className="text-sm font-medium mb-1.5">Titre FR *</div>
                                <Input value={draft.title_fr || ''} onChange={(e) => setDraft({ ...draft, title_fr: e.target.value })} data-testid="sa-page-title-fr" />
                            </div>
                            <div>
                                <div className="text-sm font-medium mb-1.5">Titre EN</div>
                                <Input value={draft.title_en || ''} onChange={(e) => setDraft({ ...draft, title_en: e.target.value })} data-testid="sa-page-title-en" />
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-4 items-end">
                            <div className="sm:col-span-2 flex items-center gap-2 text-xs text-muted-foreground">
                                Slug (URL <code>/p/{draft.slug}</code>) — non modifiable
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={draft.is_published !== false}
                                    onChange={(e) => setDraft({ ...draft, is_published: e.target.checked })}
                                    data-testid="sa-page-published" />
                                Publiée (visible dans le footer)
                            </label>
                        </div>
                        <div>
                            <div className="text-sm font-medium mb-1.5">Contenu</div>
                            <RichTextEditor
                                value={draft.content_html || ''}
                                onChange={(html) => setDraft({ ...draft, content_html: html })}
                            />
                        </div>
                        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                            <a href={`/p/${draft.slug}`} target="_blank" rel="noopener noreferrer"
                                className="text-sm text-primary hover:underline" data-testid="sa-page-preview">
                                Aperçu public ↗
                            </a>
                            <Button onClick={save} disabled={busy} className="bg-primary hover:bg-primary-hover" data-testid="sa-page-save">
                                {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                                Enregistrer
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


// =============================================================================
// PLANS CLIENTS TAB (V10) — gestion des tarifs / quotas / options par client
// =============================================================================
const PLAN_PRESETS = {
    free:         { label: '🌱 Explorer (Gratuit · permanent)', max_workspaces: 1,    max_projects: 1,    max_users: 3,    white_label_included: false, packs_included: false, billing_period: 'none' },
    essentiel:    { label: '🌿 Essentiel (39 €/mois)',          max_workspaces: 1,    max_projects: 3,    max_users: 10,   white_label_included: false, packs_included: false, billing_period: 'monthly' },
    pro:          { label: '💼 Impact (99 €/mois)',             max_workspaces: 3,    max_projects: 10,   max_users: 25,   white_label_included: true,  packs_included: true,  billing_period: 'monthly' },
    organisation: { label: '🏢 Organisation (249 €/mois)',      max_workspaces: 10,   max_projects: 9999, max_users: 100,  white_label_included: true,  packs_included: true,  billing_period: 'monthly' },
    unlimited:    { label: '🏛️ Institution (sur devis)',        max_workspaces: 9999, max_projects: 9999, max_users: 9999, white_label_included: true,  packs_included: true,  billing_period: 'monthly' },
};

function PlansTab({ auth }) {
    const [plans, setPlans] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(false);
    const [search, setSearch] = useState('');

    const reload = async () => {
        if (!auth) return;
        setLoading(true);
        try {
            const [pr, ur] = await Promise.all([
                axios.get(`${API}/super-admin/account-plans`, auth),
                axios.get(`${API}/super-admin/users`, auth),
            ]);
            setPlans(pr.data.items || []);
            setUsers(ur.data.items || []);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setLoading(false);
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [auth]);

    const startNew = () => setEditing({
        _new: true, owner_user_id: '', organization: '', plan_tier: 'free', notes: '',
        ...PLAN_PRESETS.free,
    });

    const onTierChange = (tier) => {
        const preset = PLAN_PRESETS[tier];
        setEditing(prev => ({ ...prev, plan_tier: tier, ...preset }));
    };

    const save = async () => {
        if (!editing.owner_user_id) { toast.error('Sélectionnez un utilisateur propriétaire'); return; }
        setBusy(true);
        try {
            const body = {
                owner_user_id: editing.owner_user_id,
                organization: editing.organization || null,
                plan_tier: editing.plan_tier,
                billing_period: editing.billing_period,
                max_workspaces: Number(editing.max_workspaces),
                max_projects: Number(editing.max_projects),
                max_users: Number(editing.max_users),
                white_label_included: !!editing.white_label_included,
                packs_included: !!editing.packs_included,
                notes: editing.notes || null,
                period_end: editing.period_end ? new Date(editing.period_end).toISOString() : null,
            };
            await axios.put(`${API}/super-admin/account-plans`, body, auth);
            toast.success('Plan enregistré');
            setEditing(null);
            await reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin" /></div>;

    const filtered = plans.filter(p => {
        const q = search.toLowerCase();
        return !q || (p.owner_email || '').toLowerCase().includes(q) || (p.organization || '').toLowerCase().includes(q) || (p.owner_name || '').toLowerCase().includes(q);
    });

    return (
        <div className="space-y-5" data-testid="sa-plans-tab">
            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-sm">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un client…"
                        className="pl-9" data-testid="sa-plans-search" />
                </div>
                <div className="text-xs text-muted-foreground">{plans.length} plan(s) actif(s)</div>
                <div className="flex-1" />
                <Button size="sm" onClick={startNew} className="bg-primary hover:bg-primary-hover" data-testid="sa-plans-new">
                    <Plus size={14} className="mr-1.5" /> Nouveau plan
                </Button>
            </div>

            <div className="card-soft overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-surface-muted text-left">
                        <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                            <th className="px-5 py-3 font-medium">Client</th>
                            <th className="px-5 py-3 font-medium">Plan</th>
                            <th className="px-5 py-3 font-medium text-center">Espaces</th>
                            <th className="px-5 py-3 font-medium text-center">Projets</th>
                            <th className="px-5 py-3 font-medium text-center">Users</th>
                            <th className="px-5 py-3 font-medium">Statut</th>
                            <th className="px-5 py-3 font-medium">Options</th>
                            <th className="px-5 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {filtered.length === 0 && (
                            <tr><td colSpan={8} className="px-5 py-12 text-center text-foreground/60">Aucun plan client pour le moment. Cliquez sur "Nouveau plan".</td></tr>
                        )}
                        {filtered.map(p => (
                            <tr key={p.id} className="hover:bg-surface-muted/50" data-testid={`sa-plan-row-${p.id}`}>
                                <td className="px-5 py-3">
                                    <div className="font-medium">{p.owner_name || p.owner_email || '—'}</div>
                                    <div className="text-xs text-muted-foreground">{p.organization || p.owner_email}</div>
                                </td>
                                <td className="px-5 py-3"><span className="phase-badge bg-primary-light text-primary">{p.plan_tier}</span></td>
                                <td className="px-5 py-3 text-center font-mono text-xs">
                                    <span className={p.usage_workspaces > p.max_workspaces ? 'text-red-600 font-semibold' : ''}>
                                        {p.usage_workspaces ?? 0}
                                    </span>
                                    <span className="text-muted-foreground"> / {p.max_workspaces}</span>
                                </td>
                                <td className="px-5 py-3 text-center font-mono text-xs">
                                    <span className={p.usage_projects > p.max_projects ? 'text-red-600 font-semibold' : ''}>
                                        {p.usage_projects ?? 0}
                                    </span>
                                    <span className="text-muted-foreground"> / {p.max_projects}</span>
                                </td>
                                <td className="px-5 py-3 text-center font-mono text-xs">
                                    <span className={p.usage_users > p.max_users ? 'text-red-600 font-semibold' : ''}>
                                        {p.usage_users ?? 0}
                                    </span>
                                    <span className="text-muted-foreground"> / {p.max_users}</span>
                                </td>
                                <td className="px-5 py-3">
                                    <ComplianceCell status={p.compliance_status} reason={p.compliance_reason} />
                                </td>
                                <td className="px-5 py-3 text-xs">
                                    {p.white_label_included && <span className="inline-block mr-1.5">🎨</span>}
                                    {p.packs_included && <span className="inline-block">📦</span>}
                                    {!p.white_label_included && !p.packs_included && <span className="text-muted-foreground">—</span>}
                                </td>
                                <td className="px-5 py-3 text-right">
                                    <Button size="sm" variant="ghost" onClick={() => setEditing({ ...p })} data-testid={`sa-plan-edit-${p.id}`}>
                                        <Pencil size={13} />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                {editing && (
                    <DialogContent className="max-w-xl">
                        <DialogHeader>
                            <DialogTitle className="font-heading text-2xl">{editing._new ? 'Nouveau plan client' : 'Modifier le plan'}</DialogTitle>
                            <DialogDescription>Le plan détermine les quotas + l'éligibilité automatique aux options.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            {editing._new && (
                                <div>
                                    <div className="text-sm font-medium mb-1.5">Utilisateur propriétaire *</div>
                                    <select value={editing.owner_user_id} onChange={(e) => setEditing({ ...editing, owner_user_id: e.target.value })}
                                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="sa-plan-owner">
                                        <option value="">— Choisir —</option>
                                        {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email} ({u.email})</option>)}
                                    </select>
                                </div>
                            )}
                            <div>
                                <div className="text-sm font-medium mb-1.5">Organisation</div>
                                <Input value={editing.organization || ''} onChange={(e) => setEditing({ ...editing, organization: e.target.value })} data-testid="sa-plan-org" />
                            </div>
                            <div>
                                <div className="text-sm font-medium mb-1.5">Plan tarifaire *</div>
                                <select value={editing.plan_tier} onChange={(e) => onTierChange(e.target.value)}
                                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="sa-plan-tier">
                                    {Object.entries(PLAN_PRESETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <div className="text-xs font-medium mb-1.5">Max espaces</div>
                                    <Input type="number" min="0" value={editing.max_workspaces} onChange={(e) => setEditing({ ...editing, max_workspaces: e.target.value })} data-testid="sa-plan-max-ws" />
                                </div>
                                <div>
                                    <div className="text-xs font-medium mb-1.5">Max projets</div>
                                    <Input type="number" min="0" value={editing.max_projects} onChange={(e) => setEditing({ ...editing, max_projects: e.target.value })} data-testid="sa-plan-max-proj" />
                                </div>
                                <div>
                                    <div className="text-xs font-medium mb-1.5">Max users</div>
                                    <Input type="number" min="0" value={editing.max_users} onChange={(e) => setEditing({ ...editing, max_users: e.target.value })} data-testid="sa-plan-max-users" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <div className="text-sm font-medium mb-1.5">Période de facturation</div>
                                    <select value={editing.billing_period} onChange={(e) => setEditing({ ...editing, billing_period: e.target.value })}
                                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                                        <option value="none">— Aucune (gratuit) —</option>
                                        <option value="monthly">Mensuel</option>
                                        <option value="yearly">Annuel (par an)</option>
                                        <option value="one_time">Paiement unique</option>
                                    </select>
                                </div>
                                <div>
                                    <div className="text-sm font-medium mb-1.5">Fin d'abonnement</div>
                                    <Input
                                        type="date"
                                        value={editing.period_end ? String(editing.period_end).slice(0, 10) : ''}
                                        onChange={(e) => setEditing({ ...editing, period_end: e.target.value })}
                                        data-testid="sa-plan-period-end"
                                    />
                                    <div className="text-[11px] text-muted-foreground mt-1">
                                        Rappels auto envoyés à -60j et -30j
                                    </div>
                                </div>
                            </div>
                            <div className="border-t border-border pt-4 space-y-2.5">
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={!!editing.white_label_included}
                                        onChange={(e) => setEditing({ ...editing, white_label_included: e.target.checked })}
                                        data-testid="sa-plan-wl" />
                                    🎨 Marque blanche incluse
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={!!editing.packs_included}
                                        onChange={(e) => setEditing({ ...editing, packs_included: e.target.checked })}
                                        data-testid="sa-plan-packs" />
                                    📦 Tous les Packs Kit Projet sectoriels inclus
                                </label>
                            </div>
                            <div>
                                <div className="text-xs font-medium mb-1.5">Notes internes (visible Super Admin uniquement)</div>
                                <Textarea rows={2} value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" onClick={() => setEditing(null)}>Annuler</Button>
                            <Button onClick={save} disabled={busy} className="bg-primary hover:bg-primary-hover" data-testid="sa-plan-save">
                                {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                                Enregistrer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                )}
            </Dialog>
        </div>
    );
}

